<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--payment"></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="form">
        <div class="navigators">
          <div class="navigator navigator--active">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">{{ $t(`addInvoiceModal.${"title"}`) }}</div>
          </div>
        </div>
        <div class="add__form add__form--payment">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <div class="payment">
                <div class="payment__fields">
                  <div class="payment__row">
                    <div class="form__field">
                      <VSelect class="select select--default" :class="{ 'select--error': statusHasError }"
                        :options="policyInvoiceStatuses" :reduce="(status) => status.id" label="name"
                        :placeholder="$t(`addInvoiceModal.${'status'}`)" v-model="statusId" />
                      <div class="form__error" v-if="statusHasError">
                        {{ $t(`addInvoiceModal.${"statusErrorMsg"}`) }}
                      </div>
                    </div>
                    <div class="form__field">
                      <input type="text" class="input input--default" :class="{ 'input--error': priceHasError }"
                        v-model="price" :placeholder="$t(`addInvoiceModal.${'damages'}`) + ' .- CHF'" />
                      <div class="form__error" v-if="priceHasError">
                        {{ priceErrorPlaceholder }}
                      </div>
                    </div>
                  </div>
                  <div class="payment__row">
                    <div class="form__field form__field--date">
                      <Datepicker class="input input--default input--shadow" :class="{
                        'input--error': dateHasError,
                      }" :placeholder="$t(`addInvoiceModal.${'date'}`)" :config="statics.datePickerConfig" v-model="date" />
                      <div class="form__error" v-if="dateHasError">
                        {{ $t(`addInvoiceModal.${"dateErrorMsg"}`) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="form__field">
                  <DropFile v-model="files" />
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div v-if="isRequesting" class="modal__button modal__button--submit button button--default button--loading"></div>
      <button v-else class="modal__button modal__button--submit button button--default" @click="createPolicyInvoice()">
        <span>{{ $t(`buttons.${"saveBtn"}`) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import DropFile from "@/components/DropFile";
import Loading from "@/partials/Loading";
import statics from "@/statics";
import { fetchGlobalCustomer } from "@/helpers/accountDetailHelpers";
import { useI18n } from 'vue-i18n';   

// eslint-disable-next-line no-unused-vars
const { t } = useI18n();

defineOptions({
  name: "AddInvoiceModal"
});

const store = useStore();
const route = useRoute();

// Accessing global custom elements from Vue prototype context ($checkRolePermission)
const { proxy } = getCurrentInstance();

// Reactive properties
const formLoading = ref(false);
const isRequesting = ref(false);

const files = ref([]);
const policyId = ref("");
const price = ref("");
const statusId = ref("");
const date = ref("");

const priceHasError = ref(false);
const statusHasError = ref(false);
const dateHasError = ref(false);

const priceErrorPlaceholder = ref("");

// Computed states mapped from Vuex
const policyInvoiceStatuses = computed(() => store.getters.policyInvoiceStatuses);
const invoicePolicyId = computed(() => store.getters.invoicePolicyId);

// Validation Functions
const validatePrice = () => {
  if (!price.value) {
    priceHasError.value = true;
    priceErrorPlaceholder.value = "Price is required";
  } else {
    priceHasError.value = false;
    priceErrorPlaceholder.value = "";
    if (
      statics.stringRegex.test(price.value) ||
      statics.symbolRegex.test(price.value)
    ) {
      priceHasError.value = true;
      priceErrorPlaceholder.value = "Invalid price";
    }
  }
};

const validateStatus = () => {
  statusHasError.value = !statusId.value;
};

const validateDate = () => {
  dateHasError.value = !date.value;
};

// Reactivity monitors (Watches)
watch(statusId, () => {
  validateStatus();
});

watch(price, () => {
  validatePrice();
});

watch(date, () => {
  validateDate();
});

// Structural internal methods
const closeModal = () => {
  store.commit("setShowAddInvoiceModal", false);
  store.commit("setInvoicePolicyId", null);
};

const createPolicyInvoice = async () => {
  validatePrice();
  validateStatus();
  validateDate();

  if (!statusHasError.value && !priceHasError.value && !dateHasError.value) {
    isRequesting.value = true;
    try {
      const {
        data: {
          data: { id: invoiceId },
        },
      } = await store.dispatch("createPolicyInvoice", {
        policy_id: policyId.value,
        price: price.value,
        status_id: statusId.value,
        date: date.value,
      });

      if (files.value.length) {
        await store.dispatch("addPolicyInvoiceFile", {
          invoiceId,
          files: files.value.map((file) => file.file),
        });
      }
      await fetchGlobalCustomer(route.params.id);
      isRequesting.value = false;
      
      store.commit("setToast", {
        display: true,
        type: "success",
        message: t("messages.invoiceCreatedSuccessfully"),
      });
      closeModal();
    } catch (err) {
      isRequesting.value = false; 
      store.commit("setToast", {
        display: true,
        type: "error",
        message: err.response?.data?.message || t("messages.failedToCreateInvoice"),
      });
    }
  }
};

// Initialization block (Replaces created lifecycle hook)
const initializeComponent = async () => {
  formLoading.value = true;

  if (!proxy.$checkRolePermission('policy_invoice_create')) {
    closeModal();
  } else {
    await store.dispatch("getPolicyInvoiceStatuses");
    policyId.value = invoicePolicyId.value;
  }

  formLoading.value = false;
};
initializeComponent();

// Cleanup hook
onUnmounted(() => {
  closeModal();
});
</script>
<style scoped lang="scss">
.payment,
.payment__row {
  display: flex;
}

.payment {
  align-items: flex-start;

  &__fields {
    flex: 2;
  }

  .form__field {
    flex: 1;
    margin-right: 10px;

    &--date {
      flex: initial;
      width: calc(50% - 10px);
    }
  }
}
</style>
